/**
 * v4.35.0 — Daily Review auto-redirect gate.
 *
 * Runs ONCE per browser session (per app-mount lifecycle) when the
 * user is on an authenticated page. Checks the backend gate and
 * redirects to `/welcome` exactly when the user hasn't seen today's
 * review yet and has the preference enabled.
 *
 * Design notes:
 *   • Uses sessionStorage to ensure the check runs at most once per
 *     tab session. This prevents a redirect loop and keeps subsequent
 *     navigation cheap.
 *   • Idempotent — if the user manually navigates back to the
 *     dashboard after reading the welcome page, no second redirect
 *     fires because `welcome_last_seen_date` is already today on the
 *     server side.
 *   • Skipped entirely on the public + auth-flow routes (login,
 *     reset, /welcome itself, NFC scan pages).
 *   • Calm failure — any error in the check defaults to "don't
 *     redirect" so the welcome path never blocks the user.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './auth';
import { welcomeAPI } from './api';

const SESSION_FLAG = 'yulontap-welcome-checked';

// Routes that must never redirect to /welcome. Public flows, auth flows,
// the welcome page itself, NFC tap landing pages, and anything beneath
// /respond-now or /redeem (post-tap guest flows).
const SKIP_PREFIXES = [
    '/login', '/forgot-password', '/reset-password', '/change-password',
    '/welcome',
    '/r/', '/landing/', '/review-shield/', '/global-review/', '/bridge/',
    '/staff/', '/manager/', '/demo/', '/voucher/', '/redeem', '/s/',
    '/respond-now', '/mytap/',
];

function shouldSkip(pathname) {
    if (!pathname) return true;
    return SKIP_PREFIXES.some((p) => pathname.startsWith(p));
}

export function WelcomeRedirectGate() {
    const { isAuthenticated, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (loading || !isAuthenticated) return undefined;
        if (shouldSkip(location.pathname)) return undefined;
        // Showroom demo isolation: a demo manager session (set by DemoManagerEntry)
        // must land directly in /command-center with no Daily-Review interception.
        // This flag lives only in the demo tab and never affects real users.
        try { if (sessionStorage.getItem('yulontap_demo_mode') === '1') return undefined; } catch { /* ignore */ }
        // Already checked this tab — don't poll again.
        if (sessionStorage.getItem(SESSION_FLAG) === '1') return undefined;

        let cancelled = false;
        (async () => {
            try {
                const res = await welcomeAPI.check();
                if (cancelled) return;
                // Mark this tab as checked regardless of outcome so we
                // never re-poll within a session.
                sessionStorage.setItem(SESSION_FLAG, '1');
                if (res.data?.should_redirect) {
                    navigate('/welcome', { replace: true });
                }
            } catch {
                // Calm failure — never block the user. Mark checked
                // anyway so we don't spam the endpoint.
                if (!cancelled) sessionStorage.setItem(SESSION_FLAG, '1');
            }
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated, loading, location.pathname, navigate]);

    return null;
}

/**
 * Clear the session flag — call from the logout flow so the next user
 * to log in on the same tab gets a fresh welcome-check.
 */
export function resetWelcomeGate() {
    try { sessionStorage.removeItem(SESSION_FLAG); } catch { /* ignore */ }
}
